import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import {
  signInWithEmailAndPassword,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
} from "firebase/auth";
import { doc, setDoc, getDoc, serverTimestamp } from "firebase/firestore";
import { auth, db } from "../../firebase/firebaseConfig";
import { toast } from "react-hot-toast";
import routePaths from "../../routes/routePaths";
import { useAuth } from "../../contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { pickDisplayFromUser, propagateDisplayForUser } from "../../firebase/roster";

export default function LoginPage({ isModal, onSuccess }) {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loadingGoogle, setLoadingGoogle] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { userStatus } = useAuth();

  const from = location.state?.from?.pathname || routePaths.profile;
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  // Thông báo trạng thái tài khoản (blocked/deleted)
  useEffect(() => {
    if (userStatus?.error === "blocked") toast.error(t("login.blocked"));
    if (userStatus?.error === "deleted") toast.error(t("login.deleted"));
  }, [userStatus, t]);

  // ===== Helper: cập nhật/sync user vào Firestore =====
  const upsertUserDoc = useCallback(async (user) => {
    const ref = doc(db, "users", user.uid);
    const snap = await getDoc(ref);
    const displayName = user.displayName || "";
    const firstName = displayName.split(" ")[0] || "";
    const lastName = displayName.split(" ").slice(1).join(" ") || "";
    const base = {
      uid: user.uid,
      email: user.email || "",
      firstName,
      lastName,
      photoData: user.photoURL || "",
      emailVerified: !!user.emailVerified,
      providerIds: (user.providerData || []).map(p => p.providerId),
      lastLoginAt: serverTimestamp(),
    };
    if (!snap.exists()) {
      await setDoc(ref, { ...base, createdAt: serverTimestamp(), role: "student" }, { merge: true });
    } else {
      await setDoc(ref, base, { merge: true });
    }
    const display = pickDisplayFromUser({
          ...base,
          displayName: `${base.firstName || ""} ${base.lastName || ""}`.trim(),
        });
        await propagateDisplayForUser(user.uid, display);
  }, []);

  // ===== Helper: điều hướng sau đăng nhập =====
  const afterLoginSuccess = useCallback(() => {
    toast.success(t("login.success"));
    if (onSuccess) onSuccess();
    else if (!isModal) navigate(from, { replace: true });
  }, [from, isModal, navigate, onSuccess, t]);

  // ===== Xử lý kết quả redirect (Google) =====
  useEffect(() => {
    (async () => {
      try {
        const result = await getRedirectResult(auth);
        if (result?.user) {
          await upsertUserDoc(result.user);
          toast.success(t("login.googleSuccess"));
          afterLoginSuccess();
        }
      } catch (err) {
        if (!err) return;
        toast.error(`${t("login.googleError")} ${err.message || err.code || ""}`);
      }
    })();
  }, [afterLoginSuccess, t, upsertUserDoc]);

  // ===== Email/Password =====
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await setPersistence(auth, remember ? browserLocalPersistence : browserSessionPersistence);
      const cred = await signInWithEmailAndPassword(auth, email, password);
      await upsertUserDoc(cred.user);
      afterLoginSuccess();
    } catch (err) {
      // map lỗi thân thiện hơn
      const code = err?.code || "";
      let msg = t("login.failed");
      if (code === "auth/invalid-email") msg = t("login.errInvalidEmail");
      else if (code === "auth/user-disabled") msg = t("login.errUserDisabled");
      else if (code === "auth/user-not-found") msg = t("login.errUserNotFound");
      else if (code === "auth/wrong-password") msg = t("login.errWrongPassword");
      else if (code === "auth/too-many-requests") msg = t("login.errTooManyRequests");
      toast.error(`${msg}${err?.message ? ` (${err.message})` : ""}`);
    } finally {
      if (mounted.current) setLoading(false);
    }
  };

  // ===== Google Login (popup → fallback redirect) =====
  const handleGoogleLogin = async () => {
    setLoadingGoogle(true);
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: "select_account" });
    try {
      // Ưu tiên popup
      const result = await signInWithPopup(auth, provider);
      await upsertUserDoc(result.user);
      toast.success(t("login.googleSuccess"));
      afterLoginSuccess();
    } catch (err) {
      // Fallback sang redirect nếu popup bị chặn/đóng
      const code = err?.code;
      if (
        code === "auth/popup-blocked" ||
        code === "auth/cancelled-popup-request" ||
        code === "auth/popup-closed-by-user"
      ) {
        try {
          await signInWithRedirect(auth, provider);
        } catch (redirectErr) {
          toast.error(`${t("login.googleBlocked")} ${redirectErr?.message || ""}`);
        }
      } else {
        toast.error(`${t("login.googleError")} ${err?.message || err?.code || ""}`);
      }
    } finally {
      if (mounted.current) setLoadingGoogle(false);
    }
  };

  // --- Form ---
  const formEl = (
    <form
      onSubmit={handleSubmit}
      className="bg-card p-4 sm:p-6 rounded shadow w-full max-w-sm sm:max-w-md space-y-4 border border-border"
    >
      <div className="text-center mb-3">
        <img src="/logo.png" alt="Logo" className="h-12 mx-auto mb-2" />
        <h1 className="text-xl font-semibold text-primary">{t("login.title")}</h1>
      </div>

      <input
        type="email"
        placeholder={t("login.emailPlaceholder")}
        autoComplete="username"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className="w-full border border-border px-3 py-2 rounded text-primary focus:outline-none focus:ring-2 focus:ring-primary"
        required
        disabled={loading || loadingGoogle}
      />

      <input
        type="password"
        placeholder={t("login.passwordPlaceholder")}
        autoComplete="current-password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        className="w-full border border-border px-3 py-2 rounded text-primary focus:outline-none focus:ring-2 focus:ring-primary"
        required
        disabled={loading || loadingGoogle}
      />

      <div className="flex items-center justify-between">
        <label className="flex items-center text-sm gap-1 text-primary">
          <input
            type="checkbox"
            checked={remember}
            onChange={e => setRemember(e.target.checked)}
            className="accent-primary"
            disabled={loading || loadingGoogle}
          />
          {t("login.remember")}
        </label>
        <Link to={routePaths.forgotPassword} className="text-sm text-primary hover:underline">
          {t("login.forgot")}
        </Link>
      </div>

      <button
        type="submit"
        disabled={loading || loadingGoogle}
        className="w-full bg-primary hover:bg-primary/90 disabled:opacity-60 disabled:cursor-not-allowed text-textButton py-2 rounded font-semibold shadow transition"
      >
        {loading ? t("login.loading") : t("login.loginButton")}
      </button>

      <div className="my-4 flex items-center">
        <div className="flex-1 border-t border-border" />
        <span className="mx-2 text-menu text-xs">{t("login.or")}</span>
        <div className="flex-1 border-t border-border" />
      </div>

      <button
        type="button"
        onClick={handleGoogleLogin}
        disabled={loadingGoogle || loading}
        className="w-full flex items-center justify-center gap-2 bg-[#ea4335] hover:bg-[#c63d2e] disabled:opacity-60 disabled:cursor-not-allowed text-textButton py-2 rounded font-semibold shadow transition"
      >
        <img src="/google-icon.svg" alt="Google" className="w-5 h-5" />
        {loadingGoogle ? t("login.googleLoading") : t("login.googleButton")}
      </button>

      <p className="text-center text-sm mt-3 text-primary">
        {t("login.noAccount")}{" "}
        <Link to={routePaths.userRegister} className="text-primary hover:underline font-medium">
          {t("login.registerNow")}
        </Link>
      </p>
    </form>
  );

  if (isModal) return formEl;

  return (
    <div className="flex flex-col items-center bg-bg px-2 sm:px-0 min-h-screen pt-16 sm:pt-24 pb-10">
      {formEl}
    </div>
  );
}
